import { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import {
  pointerEventsStyles,
  BodyText,
  type RadarAddress,
  theme,
  usePressableState,
  webOnlyStyles,
} from '@sg/garnish';
import type { Customer } from '@sg/graphql-schema';

import { convertRadarAddress } from '../../../../../../../hooks';
import { type MinimumAddress } from '../../../../../../../locations.types';
import { findExistingDeliveryAddress } from '../../../../../../../utils';

export const AddressSearchResult = ({
  addressPrediction,
  customerAddresses,
  onPress,
}: AddressSearchResultProps) => {
  const { formatMessage } = useIntl();
  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const existingAddress = customerAddresses
    ? findExistingDeliveryAddress(customerAddresses, {
        ...addressPrediction,
        zipCode: addressPrediction.postalCode,
      })
    : undefined;

  const { formattedAddress } = addressPrediction;

  const customerAddress = existingAddress?.name && `${existingAddress.name} - ${formattedAddress}`;
  const address = customerAddress ?? formattedAddress;

  // ─── Callbacks ───────────────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    const formattedAddress = convertRadarAddress(addressPrediction);
    onPress(formattedAddress);
  }, [addressPrediction, onPress]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={ref}
      onPress={handleOnPress}
      accessibilityRole="button"
      accessibilityLabel={formatMessage(messages.selectLocationA11yLabel, {
        text: formattedAddress,
      })}
      accessibilityHint={formatMessage(messages.selectLocationHint)}
    >
      <View
        style={[
          styles.addressSearchResult,
          webOnlyStyles({ transition: `color ${theme.transitions.base}ms` }),
          pointerEventsStyles['box-only'],
        ]}
      >
        <BodyText underline={isInteracting} numberOfLines={1}>
          {address}
        </BodyText>
      </View>
    </TouchableOpacity>
  );
};

// ──── Types ──────────────────────────────────────────────────

type AddressSearchResultProps = {
  addressPrediction: RadarAddress;
  onPress: (selectedAddress: MinimumAddress) => void;
  customerAddresses?: Customer['addresses'];
};

// ─── Constants ─────────────────────────────────────────────────────────

export const SEARCH_RESULT_HEIGHT = 56;

// ──── Styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addressSearchResult: {
    height: SEARCH_RESULT_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  selectLocationA11yLabel: {
    defaultMessage: 'Select {text}',
    description: 'Account | Addresses | Select address prediction',
  },
  selectLocationHint: {
    defaultMessage: 'Selects location',
    description: 'Account | Addresses | Select address prediction hint',
  },
});
